// ─────────────────────────────────────────────────────────────────────────────
// latex_overlay.ts — DOM overlay layer for KaTeX-typeset math.
//
// The canvas can't render LaTeX natively, and SVG foreignObject rasterisation
// can't load KaTeX's custom fonts. We solve this with a transparent DOM layer
// that sits *above* the canvas and holds one absolutely-positioned <div> per
// visible confirmed-latex block. Each div is rendered with katex.render().
//
// Usage (from BlockRenderer._drawLatexBlock):
//   latexOverlay.track(blockId, screenRect, latexSource);
// At end of frame, call commit() — blocks not tracked this frame get hidden.
// ─────────────────────────────────────────────────────────────────────────────

import katex from 'katex';

interface TrackedBlock {
  el: HTMLDivElement;
  lastSource: string;
  lastRect: { x: number; y: number; w: number; h: number };
  seenThisFrame: boolean;
}

export class LatexOverlay {
  private _layer: HTMLDivElement;
  private _blocks = new Map<string, TrackedBlock>();

  constructor(parent: HTMLElement) {
    this._layer = document.createElement('div');
    this._layer.className = 'ox-latex-overlay';
    this._layer.style.cssText = [
      'position:absolute',
      'top:0',
      'left:0',
      'width:100%',
      'height:100%',
      'pointer-events:none',
      'overflow:hidden',
      'z-index:5',
    ].join(';');
    parent.appendChild(this._layer);
  }

  /** Called once per frame, per visible confirmed-latex block. */
  track(blockId: string, rect: { x: number; y: number; w: number; h: number }, source: string): void {
    let entry = this._blocks.get(blockId);
    if (!entry) {
      const el = document.createElement('div');
      el.style.cssText = [
        'position:absolute',
        'display:flex',
        'align-items:center',
        'justify-content:flex-start',
        'overflow:hidden',
        'color:#3a2a10',
        'pointer-events:none',
        'padding:2px 6px',
        'box-sizing:border-box',
      ].join(';');
      this._layer.appendChild(el);
      entry = { el, lastSource: '', lastRect: { x: -1, y: -1, w: -1, h: -1 }, seenThisFrame: true };
      this._blocks.set(blockId, entry);
    }
    entry.seenThisFrame = true;

    // Re-render KaTeX only if the source changed
    if (entry.lastSource !== source) {
      try {
        katex.render(source, entry.el, {
          throwOnError: false,
          displayMode: true,
          output: 'html',
          errorColor: '#cc3333',
        });
      } catch {
        entry.el.textContent = source;
      }
      entry.lastSource = source;
    }

    // Position + size (only update CSS if changed to avoid style thrash)
    const r = entry.lastRect;
    if (r.x !== rect.x || r.y !== rect.y || r.w !== rect.w || r.h !== rect.h) {
      entry.el.style.left    = `${rect.x}px`;
      entry.el.style.top     = `${rect.y}px`;
      entry.el.style.width   = `${rect.w}px`;
      entry.el.style.height  = `${rect.h}px`;
      // Auto-scale font size to fit bbox height (KaTeX scales relative to font-size)
      const fontPx = Math.max(10, Math.min(28, rect.h * 0.55));
      entry.el.style.fontSize = `${fontPx}px`;
      entry.lastRect = { ...rect };
    }
    entry.el.style.visibility = 'visible';
  }

  /** Call at start of each frame to reset visibility flags. */
  beginFrame(): void {
    for (const entry of this._blocks.values()) {
      entry.seenThisFrame = false;
    }
  }

  /** Call at end of each frame — hides any blocks not tracked this frame. */
  commit(): void {
    for (const [id, entry] of this._blocks) {
      if (!entry.seenThisFrame) {
        entry.el.style.visibility = 'hidden';
        // If block is stale for a while, could GC here — keep simple for now
        if (entry.lastSource === '__evict__') {
          this._layer.removeChild(entry.el);
          this._blocks.delete(id);
        }
      }
    }
  }

  /** Remove a block's overlay entirely (e.g. on delete). */
  evict(blockId: string): void {
    const entry = this._blocks.get(blockId);
    if (entry) {
      entry.el.style.visibility = 'hidden';
      entry.lastSource = '__evict__';
    }
  }
}

// ── Module-level singleton ───────────────────────────────────────────────────
// BlockRenderer picks this up via getLatexOverlay(). It's optional — if unset,
// _drawLatexBlock falls back to the monospace source display.

let _instance: LatexOverlay | null = null;
export function setLatexOverlay(overlay: LatexOverlay | null): void { _instance = overlay; }
export function getLatexOverlay(): LatexOverlay | null { return _instance; }
